import { CircularProgress } from "@material-ui/core";
import { Padding } from "@mui/icons-material";
import { table } from "console";
import DataTable from "react-data-table-component";
// import "react-data-table-component-extensions/dist/index.css";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { useAppSelector } from "../../../Redux/hooks";

const customStyles = {
  headCells: {
    style: {
      fontSize: "14px",
      width: '100% !important',
      justifyContent: "left",
      whiteSpace: "wrap",
      overflow: "hidden",
      color: "white",
      borderBottom: "1px solid #ffffff33 !important",
      fontWeight: "600",
      backgroundColor: "transparent",
      paddingBottom: "12px",
      paddingTop: "16px",
    },
  },
  cells: {
    style: {
      justifyContent: "left",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      color: "white",
      fontSize: '16px',
      border: "none",
      cursor: 'pointer',
      padding: "12px 8px",
      transition: "all 0.1s ease-in-out",
      backgroundColor: "transparent",
    },
  },
  rows: {
    style: {
      borderColor: "transparent !important",
      backgroundColor: 'transparent',
      width: '96% !important',
      borderRadius: "8px",
      margin: '2px 8px',
      padding: "4px",
      transition: "all 0.3s ease",
      cursor: 'pointer',
      outline: "none !important",
      '&:hover': {
        border: "1px solid #4A7AF2 !important",
        borderColor: "#4A7AF2 !important",
        backgroundColor: '#4A7AF21A !important',
        transform: 'translateY(-1px)',
        boxShadow: '0 2px 8px rgba(74, 122, 242, 0.15) !important',
        outline: "none !important",
      },
      '&:nth-child(even)': {
        backgroundColor: 'rgba(255, 255, 255, 0.02)',
        border: "1px solid transparent !important",
        borderColor: "transparent !important",
      },
      '&:nth-child(odd)': {
        backgroundColor: 'transparent',
        border: "1px solid transparent !important",
        borderColor: "transparent !important",
      },
      '&:nth-child(even):hover': {
        border: "1px solid #4A7AF2 !important",
        borderColor: "#4A7AF2 !important",
        backgroundColor: '#4A7AF21A !important',
        transform: 'translateY(-1px)',
        boxShadow: '0 2px 8px rgba(74, 122, 242, 0.15) !important',
      },
      '&:nth-child(odd):hover': {
        border: "1px solid #4A7AF2 !important",
        borderColor: "#4A7AF2 !important",
        backgroundColor: '#4A7AF21A !important',
        transform: 'translateY(-1px)',
        boxShadow: '0 2px 8px rgba(74, 122, 242, 0.15) !important',
      },
    },
  },
  headRow: {
    style: {
      backgroundColor: 'transparent',
    },
  },
};

interface CommonDataTableProps {
  data: any;
  totalRows: number;
  setPage: (page: number) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
  columns: any[];
  title: string;
  rowNavigateTo?: ((row: any) => { url: string; state?: any }) | string;
  selectableRows?: boolean;
  selectedRows?: any[];
  onSelectedRowsChange?: (selectedRows: any) => void;
  [x: string]: any;
}

export function CommonDataTable({
  data,
  totalRows,
  setPage,
  setRowsPerPage,
  columns,
  title,
  rowNavigateTo,
  selectableRows = false,
  selectedRows = [],
  onSelectedRowsChange,
  ...props
}: CommonDataTableProps) {
  const { isLoading } = useAppSelector((state) => state.loading);
  const navigation = useNavigate();
  const tableRef = useRef<HTMLDivElement>(null);

  // Manual row click handler using DOM events
  useEffect(() => {
    const addClickHandlers = () => {
      // console.log('Adding click handlers, tableRef:', tableRef.current);
      // console.log('Data length:', data?.length);

      if (tableRef.current && data?.length > 0) {
        // Try different selectors to find rows
        const possibleSelectors = [
          'tbody tr',
          'tr[data-tid="dataTable-row-0"]',
          'table tr',
          '.rdt_TableBody tr',
          '.rdt_TableBodyRowGroup tr'
        ];

        let rows: NodeList | null = null;
        for (const selector of possibleSelectors) {
          rows = tableRef.current.querySelectorAll(selector);
          // console.log(`Selector "${selector}" found ${rows.length} rows`);
          if (rows.length > 0) break;
        }

        if (!rows || rows.length === 0) {
          // If no rows found, try event delegation
          // console.log('No rows found, using enhanced event delegation');

          // Log the DOM structure to understand what elements exist
          const allElements = Array.from(tableRef.current.querySelectorAll('*'));
          // console.log('Available elements:', allElements.slice(0, 20).map(el => ({
          //   tag: el.tagName,
          //   classes: (el as HTMLElement).className,
          //   id: el.id,
          //   role: el.getAttribute('role'),
          //   dataTid: el.getAttribute('data-tid')
          // })));

          tableRef.current.addEventListener('click', (event) => {
            // console.log('Click event detected, target:', event.target);

            const target = event.target as HTMLElement;

            // Check if the click originated from a button, link, or other interactive element
            const isButton = target.closest('button, a, [role="button"], input, select, textarea, .MuiButton-root, .MuiIconButton-root, svg, path');

            // If click is on an interactive element, don't navigate
            if (isButton) {
              // console.log('Click on interactive element, ignoring row navigation');
              return; // Don't prevent default - let the button handle it
            }

            // Look specifically for react-data-table rows (filter out Bootstrap rows)
            const potentialRowElements = [
              target.closest('[class*="rdt_TableRow"]'),
              target.closest('[id^="row-"]'),
              target.closest('[class*="rdt_TableBodyRow"]'),
              target.closest('[role="row"]'),
              target.closest('[data-tid*="row"]'),
            ].filter((el): el is Element => el !== null);

            // Filter out Bootstrap row elements
            const filteredElements = potentialRowElements.filter((el): el is Element => {
              if (!el) return false;
              const className = el.className?.toString?.() || '';
              return !className.includes('dark-tbl') && !className.includes('no-border');
            });

            // console.log('Potential row elements found:', potentialRowElements);
            // console.log('Filtered elements (excluding Bootstrap rows):', filteredElements);

            // Use the best matching element (prefer rdt_TableRow)
            const actualRow = filteredElements.find((el): el is Element => {
              if (!el) return false;
              const className = el.className?.toString?.() || '';
              return className.includes('rdt_TableRow');
            }) || filteredElements[0];

            if (actualRow) {
              // console.log('Using row element:', actualRow);

              // Find all table rows (specifically rdt_TableRow elements)
              if (!tableRef.current) {
                // console.log('Table ref is null, skipping navigation');
                return;
              }

              const allTableRows = Array.from(tableRef.current.querySelectorAll('[class*="rdt_TableRow"]'));
              const rowIndex = allTableRows.indexOf(actualRow);

              // console.log('Found row index:', rowIndex, 'out of', allTableRows.length, 'table rows');

              if (rowIndex >= 0 && rowIndex < data.length && data[rowIndex]) {
                // console.log('Valid row click:', data[rowIndex]);
                handleNavigation(data[rowIndex]);
              }
            }
          });
          return;
        }

        // console.log(`Found ${rows.length} rows, adding click handlers`);

        rows.forEach((row, index) => {
          const existingHandler = (row as HTMLElement).onclick;
          // console.log(`Row ${index} existing handler:`, existingHandler);

          // Add new click handler with capture phase to check early
          row.addEventListener('click', (event) => {
            // console.log(`Row ${index} clicked!`, event.target);

            // Check if the click originated from a button, link, or other interactive element
            const target = event.target as HTMLElement;
            const isButton = target.closest('button, a, [role="button"], input, select, textarea, .MuiButton-root, .MuiIconButton-root, svg, path');

            // If click is on an interactive element, don't navigate
            if (isButton) {
              // console.log('Click on interactive element, ignoring row navigation');
              return; // Don't prevent default or stop propagation - let the button handle it
            }

            // Only prevent default and navigate if it's not a button click
            const rowData = data[index];
            if (rowData) {
              // console.log('Manual row click:', rowData);
              event.preventDefault();
              event.stopPropagation();
              handleNavigation(rowData);
            }
          }, false);
        });
      }
    };

    // Add handlers with longer delay to ensure DOM is ready
    const timer = setTimeout(addClickHandlers, 500);
    return () => clearTimeout(timer);
  }, [data]);

  const handleNavigation = (row: any) => {
    if (rowNavigateTo) {
      if (typeof rowNavigateTo === "function") {
        const { url, state } = rowNavigateTo(row);
        navigation(url, { state });
      } else if (typeof rowNavigateTo === "string") {
        navigation(rowNavigateTo, { state: row });
      }
    }
  };

  // Old unused functions removed - using DOM event delegation instead

  return (
    <div style={{ position: "relative", backgroundColor: 'transparent !important' }}>
      <style>
        {`
        .rdt_TableWrapper,
        .rdt_Table,
        .rdt_TableHead,
        .rdt_TableHeadRow,
        .rdt_TableBody,
        .rdt_TableRow,
        .rdt_Pagination,
        [class*="sc-"] {
          background-color: transparent !important;
        }
        
        /* Progress wrapper fix */
        div[class*="ProgressWrapper"],
        div[role="progressbar"],
        .rdt_Table > div:first-child {
          background-color: transparent !important;
        }
        .rdt_TableCol input[type="checkbox"],
        .rdt_TableCell input[type="checkbox"] {
          appearance: none;
          -webkit-appearance: none;

          width: 24px;
  height: 24px;

  border-radius: 6px;
  border: 1.5px solid rgba(255, 255, 255, 0.35);
  background-color: transparent;

  cursor: pointer;
  position: relative;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  transition: all 0.15s ease;
}

/* Hover */
.rdt_TableCol input[type="checkbox"]:hover,
.rdt_TableCell input[type="checkbox"]:hover {
  border-color: rgba(255, 255, 255, 0.6);
}

/* Checked */
.rdt_TableCol input[type="checkbox"]:checked,
.rdt_TableCell input[type="checkbox"]:checked {
  background-color: #4A7AF2;
  border-color: #4A7AF2;
}

/* Tick */
.rdt_TableCol input[type="checkbox"]:checked::after,
.rdt_TableCell input[type="checkbox"]:checked::after {
  content: "";
  position: absolute;

  width: 9px;
  height: 5px;

  border-left: 2px solid #ffffff;
  border-bottom: 2px solid #ffffff;

  transform: rotate(-45deg);
}

.rdt_TableWrapper, .rdt_Table, .rdt_TableHead, .rdt_TableHeadRow, .rdt_TableBody, .rdt_TableRow, .rdt_Pagination{
color: white !important;
}

.custom-table-container button:disabled {
fill:#a3a3a370 !important;
color:#a3a3a370 !important;
}

.custom-table-container button {
fill:#FFFFFF !important;
color:#FFFFFF !important;
}


      `}
      </style>
      <div ref={tableRef} style={{ maxHeight: "600px" }}>
        <DataTable
          columns={columns}
          data={data}
          style={{ backgroundColor: 'transparent', maxHeight: '600px' }}
          fixedHeaderScrollHeight="600px"
          onChangePage={(value) => {
            setPage(value);
          }}
          responsive={true}
          fixedHeader={true}
          highlightOnHover={true}
          pointerOnHover={true}
          dense={false}
          persistTableHead={false}
          noHeader={false}
          onChangeRowsPerPage={setRowsPerPage}
          paginationTotalRows={totalRows}
          progressPending={isLoading}
          customStyles={customStyles}
          selectableRows={selectableRows ? 'multiple' : false}
          selectedRows={selectableRows ? { selectedCount: selectedRows.length } : undefined}
          selectableRowSelected={props.selectableRowSelected}
          onSelectedRowsChange={
            selectableRows && onSelectedRowsChange
              ? (state) => onSelectedRowsChange(state.selectedRows)
              : undefined
          }
          progressComponent={
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "40px",
                backgroundColor: "transparent",
                minHeight: "200px",
              }}
            >
              <CircularProgress size="40px" style={{ color: "#4A7AF2" }} />
            </div>
          }
          noDataComponent={
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                padding: '24px',
                color: "white",
                fontSize: "16px",
                backgroundColor: "transparent",
              }}
            >
              There are no records to display
            </div>
          }
          {...props}
        />
      </div>
    </div>
  );
}
